import { defineStore } from 'pinia'
import { store } from '../index'
import { useTagsViewStore } from './tagsView'
import router from '@/router'
import api from '@/apiService/modules/api'
import { cloneDeep } from 'lodash-es'
import { flatMultiLevelRoutes } from '@/utils/routerHelper'

interface UserState {
  userInfo?: API.AdminInfoDto
  token: string
  // 权限相关
  permissions: string[]
  buttonPermissions: string[]
  isPermissionLoading: boolean
  isPermissionLoaded: boolean
  // 路由相关
  routers: AppRouteRecordRaw[]
  addRouters: AppRouteRecordRaw[]
  isAddRouters: boolean
  menuTabRouters: AppRouteRecordRaw[]
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return {
      userInfo: undefined,
      token: '',
      // 权限相关
      permissions: [],
      buttonPermissions: [],
      isPermissionLoading: false,
      isPermissionLoaded: false,
      // 路由相关
      routers: [],
      addRouters: [],
      isAddRouters: false,
      menuTabRouters: [],
    }
  },
  getters: {
    getToken(): string {
      return this.token
    },
    getUser(): API.AdminInfoDto | undefined {
      return this.userInfo
    },
    // 权限相关 getters
    getPermissions(): string[] {
      return this.permissions
    },
    getButtonPermissions(): string[] {
      return this.buttonPermissions
    },
    hasPermission: (state) => (code: string) => {
      // 权限列表为空时，允许访问所有
      if (state.permissions.length === 0) {
        return true
      }
      return state.permissions.includes(code)
    },
    hasButtonPermission: (state) => (code: string) => {
      // 按钮权限列表为空时，允许所有操作
      if (state.buttonPermissions.length === 0) {
        return true
      }
      return state.buttonPermissions.includes(code)
    },
    getIsPermissionLoading(): boolean {
      return this.isPermissionLoading
    },
    getIsPermissionLoaded(): boolean {
      return this.isPermissionLoaded
    },
    // 路由相关 getters
    getRouters(): AppRouteRecordRaw[] {
      return this.routers
    },
    getAddRouters(): AppRouteRecordRaw[] {
      console.log("🚀 ~ flatMultiLevelRoutes(cloneDeep(this.addRouters)):", flatMultiLevelRoutes(cloneDeep(this.addRouters)))
      return flatMultiLevelRoutes(cloneDeep(this.addRouters))
    },
    getIsAddRouters(): boolean {
      return this.isAddRouters
    },
    getMenuTabRouters(): AppRouteRecordRaw[] {
      return this.menuTabRouters
    },
  },
  actions: {
    setToken(token: string) {
      this.token = token
    },
    setUserInfo(userInfo?: API.AdminInfoDto) {
      this.userInfo = userInfo
    },
    // 权限相关 actions
    /**
     * 开始加载权限
     */
    startLoadingPermissions() {
      this.isPermissionLoading = true
    },
    /**
     * 初始化权限：从 userInfo 中获取用户权限
     */
    initPermissions() {
      const permissions = this.userInfo?.permissions || []
      const buttonPermissions = this.userInfo?.buttonPermissions || []
      // permissions 是权限编码数组，对应路由的 name 字段
      this.permissions = permissions
      this.buttonPermissions = buttonPermissions
      this.isPermissionLoading = false
      this.isPermissionLoaded = true
    },
    /**
     * 清除权限
     */
    clearPermissions() {
      this.permissions = []
      this.buttonPermissions = []
      this.isPermissionLoading = false
      this.isPermissionLoaded = false
    },
    /**
     * 刷新用户信息和权限
     * 登录成功后调用，或者页面刷新时调用
     */
    async refreshUserInfo() {
      try {
        if (!this.token) {
          return
        }

        // 开始加载权限
        this.startLoadingPermissions()

        const response = await api.auth.adminAuthControllerGetUserInfo()
        this.setUserInfo(response.data)

        // 权限初始化完成
        this.initPermissions()
      } catch (error) {
        console.error('刷新用户信息失败:', error)
        // 如果获取失败，可能 token 过期，需要重新登录
        await this.reset()
      }
    },
    // 路由相关 actions
    setIsAddRouters(state: boolean): void {
      this.isAddRouters = state
    },
    setMenuTabRouters(routers: AppRouteRecordRaw[]): void {
      this.menuTabRouters = routers
    },
    setRouters(routers: AppRouteRecordRaw[]): void {
      this.routers = routers
    },
    setAddRouters(routers: AppRouteRecordRaw[]): void {
      this.addRouters = routers
    },
    logoutConfirm() {
    },
    async reset() {
      const tagsViewStore = useTagsViewStore()
      tagsViewStore.delAllViews()

      // 清除权限
      this.clearPermissions()

      // 清除路由
      this.routers = []
      this.addRouters = []
      this.isAddRouters = false
      this.menuTabRouters = []

      this.setToken('')
      this.setUserInfo(undefined)
      const currentPath = router.currentRoute.value.fullPath
      const redirect = currentPath && currentPath !== '/login' ? `?redirect=${encodeURIComponent(currentPath)}` : ''
      router.replace(`/login${redirect}`)
    },
    logout() {
      this.reset()
    },
  },
  persist: true
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
